/**
 * Inicialização do i18next.
 *
 * Namespaces carregam sob demanda por import dinâmico: cada tela puxa só o
 * seu, e o inglês só entra no bundle de quem troca o idioma.
 *
 * O idioma inicial vem de src/i18n/idioma.js (regra explícita, sem detector).
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import resourcesToBackend from 'i18next-resources-to-backend';
import { IDIOMAS, PADRAO, resolver, persistir, conferirNoAparelho } from './idioma';

export const NAMESPACES = [
  'comum',
  'inicio',
  'dieta',
  'treino',
  'scanbody',
  'analisecorporal',
  'scanfood',
  'perfil',
  'social',
  'gamificacao',
  'camera',
  'config',
  'paywall',
  'legal',
  'onboarding',
  'auth',
  'erros',
  'notificacoes',
  'progresso',
  'competicao',
  'receitas',
  'moedas',
  'caminho',
  'educacao',
  'clubes',
];

const inicial = resolver();

i18n
  .use(
    resourcesToBackend(
      (idioma, namespace) => import(`./${idioma}/${namespace}.json`)
    )
  )
  .use(initReactI18next)
  .init({
    lng: inicial,
    fallbackLng: PADRAO,
    supportedLngs: IDIOMAS,
    load: 'currentOnly',

    // NÃO reativar `nonExplicitSupportedLngs`.
    // Com ela, o i18next reduz o código a "pt" antes de conferir
    // `supportedLngs` (i18next/LanguageUtils.isSupportedCode). Como a lista
    // aqui é ['pt-BR','en'], "pt" não está nela: o pt-BR era considerado
    // NÃO suportado, `i18n.languages` ficava [], o backend nunca era chamado
    // e TODA a UI mostrava a chave crua ("acao.compartilhar", "abas.clubes").
    // A flag também era desnecessária: `normalizar()` em idioma.js já reduz
    // qualquer tag BCP-47 a exatamente 'pt-BR' ou 'en' antes de chegar aqui.

    ns: ['comum'],
    defaultNS: 'comum',
    fallbackNS: 'comum',

    interpolation: {
      escapeValue: false, // o React já escapa
    },

    react: {
      // sem Suspense: a tela não pode piscar em branco esperando um JSON.
      // Enquanto carrega, o i18next devolve a chave e o texto entra logo em
      // seguida — mais seguro num app que troca de aba o tempo todo.
      useSuspense: false,

      // ...mas "logo em seguida" precisa de um gatilho. O padrão do
      // react-i18next é `bindI18nStore: ''`, ou seja, o componente NÃO
      // re-renderiza quando um namespace chega depois da montagem. Com
      // namespaces carregados sob demanda, quem montou antes do JSON ficaria
      // com a chave crua na tela até o próximo render por outro motivo.
      bindI18nStore: 'added',
    },

    returnEmptyString: false,
    saveMissing: false,
  });

// Guarda de regressão: com a cadeia de idiomas vazia o i18next não pede nada
// ao backend e a interface inteira vira chave crua. Isso já aconteceu uma vez
// (nonExplicitSupportedLngs).
//
// A conferência PRECISA esperar o evento `initialized`: `init()` é assíncrono
// (initImmediate), e logo depois da chamada `i18n.languages` ainda está
// vazio mesmo quando tudo está certo — conferir na hora dá falso positivo.
//
// E só avisa: não derruba o app. Um throw no escopo do módulo quebraria o
// boot inteiro por causa de um diagnóstico, que é pior que o problema que
// ele tenta apontar. O sintoma real (a UI inteira em chaves) já é visível.
i18n.on('initialized', () => {
  if (i18n.languages && i18n.languages.length > 0) return;
  console.error(
    `[i18n] cadeia de idiomas vazia para "${i18n.language || inicial}". ` +
    'Nenhum namespace será carregado e a UI vai mostrar as chaves. ' +
    'Confira supportedLngs em src/i18n/idioma.js e as opções de init.'
  );
});

// Guarda de regressão 2: namespace que NÃO CARREGA falha calado.
//
// O sintoma é só a tela mostrando a chave crua ("mapa.abertura"), que é
// idêntico ao de chave faltando no JSON — e manda quem investiga para o lugar
// errado, conferindo um arquivo que está inteiro e correto. O i18next emite um
// `warn` pelo backendConnector, mas ele se perde no meio do console e não diz
// nada sobre o que costuma causar isso.
//
// O CASO QUE MOTIVOU: `educacao.json` nasceu como arquivo NOVO. O import
// dinâmico daqui de cima vira um contexto do webpack montado quando o servidor
// de desenvolvimento SOBE; um servidor que já estava rodando não enxerga o
// arquivo novo, e só aquele namespace falha enquanto todos os outros, que já
// existiam, seguem funcionando. Foi exatamente o que aconteceu, e nada na tela
// dizia isso.
i18n.on('failedLoading', (idioma, namespace, motivo) => {
  console.error(
    `[i18n] namespace "${namespace}" (${idioma}) NÃO CARREGOU: ` +
    `${motivo?.message || motivo}\n` +
    '  As telas desse namespace vão mostrar a chave crua. A chave no JSON ' +
    'está certa — o que falhou foi buscar o arquivo.\n' +
    `  1. Arquivo NOVO? O contexto do import dinâmico é montado quando o dev ` +
    'server sobe: reinicie o `npm start`.\n' +
    `  2. src/i18n/${idioma}/${namespace}.json existe e é JSON válido?\n` +
    '  3. Em app empacotado/servido: cache ou service worker antigo servindo ' +
    'um chunk que não existe mais.'
  );
});

/** Troca o idioma, aplica na hora e persiste. */
export async function trocarIdioma(idioma) {
  if (!IDIOMAS.includes(idioma)) return;
  persistir(idioma);
  await i18n.changeLanguage(idioma);
  try {
    document.documentElement.setAttribute('lang', idioma);
  } catch {
    /* ignora */
  }
}

// marca o idioma no <html> desde o boot (acessibilidade e hifenização)
try {
  document.documentElement.setAttribute('lang', inicial);
} catch {
  /* ignora */
}

// Depois do boot, confere o idioma do sistema pelo plugin nativo.
// Só age quando a pessoa nunca escolheu e o aparelho discorda do WebView.
conferirNoAparelho().then((corrigido) => {
  if (corrigido && corrigido !== i18n.language) {
    i18n.changeLanguage(corrigido);
    document.documentElement.setAttribute('lang', corrigido);
  }
});

export default i18n;
